import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Package, CreditCard, Mail, MapPin, Info, Printer, Truck } from 'lucide-react';
import Layout from '../Layout';
import { getSaleOrderByDocumentId } from '../../helpers/saleOrder';
import { showToast } from '../../helpers/customToast';

const SaleConfirmationPage = () => {
  const location = useLocation();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const initializeAttempted = useRef(false);

  useEffect(() => {
    const initializePage = async () => {
      // Skip if initialization was already attempted
      if (initializeAttempted.current) {
        return;
      }
      initializeAttempted.current = true;

      try {
        setLoading(true);
        const { orderId, documentId, paymentConfirmed } = location.state || {};

        if (!orderId || !documentId) {
          showToast('Commande introuvable', 'error');
          return;
        }

        const orderData = await getSaleOrderByDocumentId(documentId);
        if (!orderData) {
          showToast('Impossible de charger les détails de la commande', 'error');
          return;
        }

        setOrder(orderData);
      } catch (error) {
        console.error('Error initializing confirmation page:', error);
        showToast('Une erreur est survenue', 'error');
      } finally {
        setLoading(false);
      }
    };

    initializePage();
  }, [location]);

  const formatDate = (dateString) => {
    const options = { year: 'numeric', month: 'long', day: 'numeric' };
    return new Date(dateString).toLocaleDateString('fr-FR', options);
  };

  const handlePrint = () => {
    window.print();
  };

  if (loading) {
    return (
      <Layout>
        <div className="container my-5">
          <div className="text-center">
            <div className="spinner-border" role="status">
              <span className="visually-hidden">Loading...</span>
            </div>
          </div>
        </div>
      </Layout>
    );
  }

  if (!order) {
    return (
      <Layout>
        <div className="container my-5">
          <div className="alert alert-danger" role="alert">
            Commande introuvable
          </div>
          <Link to="/vente" className="btn btn-primary">
            Retour à la boutique
          </Link>
        </div>
      </Layout>
    );
  }

  const renderPaymentStatus = () => {
    if (location.state?.paymentConfirmed || order.payment_status === 'paid') {
      return <span className="badge bg-success">Payée</span>;
    }
    return <span className="badge bg-warning">En attente de paiement</span>;
  };

  const renderOrderStatus = () => {
    const statusLabels = {
      'pending': 'En attente',
      'confirmed': 'Confirmée',
      'processing': 'En préparation',
      'shipped': 'Expédiée',
      'delivered': 'Livrée',
      'cancelled': 'Annulée'
    };

    const statusColors = {
      'pending': 'warning',
      'confirmed': 'info',
      'processing': 'primary',
      'shipped': 'success',
      'delivered': 'success',
      'cancelled': 'danger'
    };

    return (
      <span className={`badge bg-${statusColors[order.order_status] || 'secondary'}`}>
        {statusLabels[order.order_status] || order.order_status}
      </span>
    );
  };

  return (
    <Layout className="main-layout">
      <div className="container my-5 print-content">
        <div className="row mb-4">
          <div className="col-12 d-flex justify-content-between align-items-center">
            <h2>Confirmation de commande</h2>
            <button
              onClick={handlePrint}
              className="btn btn-outline-secondary d-print-none"
            >
              <Printer size={18} className="me-2" />
              Imprimer
            </button>
          </div>
        </div>

        <div className="row">
          <div className="col-12 mb-4">
            <div className="alert alert-success" role="alert">
              <h4 className="alert-heading">Merci pour votre commande !</h4>
              <p>Votre commande a été enregistrée avec succès. Un email de confirmation vous a été envoyé.</p>
            </div>
          </div>

          <div className="col-lg-8">
            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">Détails de la commande</h3>
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Numéro de commande:</strong>
                  </div>
                  <div className="col-sm-8">
                    #{order.order_number}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Date de commande:</strong>
                  </div>
                  <div className="col-sm-8">
                    {formatDate(order.createdAt)}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Statut de la commande:</strong>
                  </div>
                  <div className="col-sm-8">
                    {renderOrderStatus()}
                  </div>
                </div>
                <div className="row mb-3">
                  <div className="col-sm-4">
                    <strong>Statut du paiement:</strong>
                  </div>
                  <div className="col-sm-8">
                    {renderPaymentStatus()}
                  </div>
                </div>
                {order.tracking_number && (
                  <div className="row mb-3">
                    <div className="col-sm-4">
                      <strong>Numéro de suivi:</strong>
                    </div>
                    <div className="col-sm-8">
                      <code>{order.tracking_number}</code>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">Coordonnées de livraison</h3>
                <p>
                  <strong>{order.user_details.first_name} {order.user_details.last_name}</strong><br />
                  {order.user_details.address_1}<br />
                  {order.user_details.address_2 ? `${order.user_details.address_2}<br />` : ''}
                  {order.user_details.postal_code} {order.user_details.city}<br />
                  {order.user_details.country}<br />
                  &nbsp;<br />
                  Email: {order.user_details.email}<br />
                  Tél : {order.user_details.phone}
                </p>
              </div>
            </div>

            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">Articles commandés</h3>
                {order.items && order.items.map((item, index) => (
                  <div key={index} className="card mb-3">
                    <div className="card-body">
                      <div className="row">
                        <div className="col-md-8">
                          <h5 className="card-title">
                            {item.product_title}
                            {item.quantity > 1 && <span className="ms-2 badge bg-secondary">x{item.quantity}</span>}
                          </h5>
                          <p className="card-text">
                            <Package className="me-2" size={16} />
                            {item.is_bulk ? `Lot de ${item.bulk_quantity}` : 'Unité'}
                            {item.quantity > 1 && ` - ${item.quantity} ${item.is_bulk ? 'lots' : 'unités'}`}
                          </p>
                          <small className="text-muted">
                            Total d'articles: {item.total_quantity}
                          </small>
                        </div>
                        <div className="col-md-4 text-end">
                          <p className="mb-0">
                            <strong>{item.total_price.toFixed(2)}€</strong>
                            {item.quantity > 1 && (
                              <small className="text-muted d-block">
                                ({item.unit_price}€ x {item.quantity})
                              </small>
                            )}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {order.comments && (
              <div className="card mb-4">
                <div className="card-body">
                  <h3 className="card-title">Commentaires</h3>
                  <div className="card">
                    <div className="card-body">
                      <p>{order.comments}</p>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className="col-lg-4">
            <div className="card mb-4">
              <div className="card-body">
                <h3 className="card-title">Résumé de la commande</h3>
                <div className="d-flex justify-content-between mb-3">
                  <span>Sous-total</span>
                  <strong>{order.subtotal.toFixed(2)}€</strong>
                </div>
                <div className="d-flex justify-content-between mb-3">
                  <span>
                    <Truck size={16} className="me-1" />
                    Livraison <small>({order.delivery_method === 'home_delivery' ? 'À domicile' : 'Point relais'})</small>
                  </span>
                  <strong>{order.delivery_price > 0 ? `${order.delivery_price.toFixed(2)}€` : 'Gratuite'}</strong>
                </div>
                {order.discount_amount > 0 && (
                  <div className="d-flex justify-content-between mb-3">
                    <span>Réduction</span>
                    <strong className="text-success">-{order.discount_amount.toFixed(2)}€</strong>
                  </div>
                )}
                <hr />
                <div className="d-flex justify-content-between mb-3">
                  <span><strong>Total</strong></span>
                  <strong>{order.total.toFixed(2)}€</strong>
                </div>
              </div>
            </div>

            <div className="card mb-4 d-print-none">
              <div className="card-body">
                <h3 className="card-title">Prochaines étapes</h3>
                <ol className="list-unstyled">
                  {!location.state?.paymentConfirmed && order.payment_status !== 'paid' && (
                    <li className="mb-3">
                      <div className="d-flex">
                        <CreditCard size={24} className="me-3 text-primary" />
                        <div>
                          <strong>Paiement</strong><br />
                          Votre paiement sera traité sous peu
                        </div>
                      </div>
                    </li>
                  )}
                  <li className="mb-3">
                    <div className="d-flex">
                      <Mail size={24} className="me-3 text-primary" />
                      <div>
                        <strong>Confirmation</strong><br />
                        Un email de confirmation vous a été envoyé
                      </div>
                    </div>
                  </li>
                  <li className="mb-3">
                    <div className="d-flex">
                      <Package size={24} className="me-3 text-primary" />
                      <div>
                        <strong>Préparation</strong><br />
                        Votre commande sera préparée sous 24-48h
                      </div>
                    </div>
                  </li>
                  <li className="mb-3">
                    <div className="d-flex">
                      <Truck size={24} className="me-3 text-primary" />
                      <div>
                        <strong>Expédition</strong><br />
                        Vous recevrez un email avec le numéro de suivi
                      </div>
                    </div>
                  </li>
                </ol>
              </div>
            </div>

            {/* Delivery info */}
            <div className="card mb-4">
              <div className="card-body">
                <h5 className="card-title">
                  <Truck size={18} className="me-2" />
                  Informations de livraison
                </h5>
                <ul className="list-unstyled mb-0 small">
                  <li>• Expédition sous 24-48h ouvrées</li>
                  <li>• Livraison en 2-5 jours ouvrés</li>
                  <li>• Suivi de colis inclus</li>
                  <li>• Emballage soigné et sécurisé</li>
                </ul>
              </div>
            </div>
          </div>
        </div>

        <div className="text-center mt-4 d-print-none">
          <Link to="/vente" className="btn btn-primary me-3">
            Continuer mes achats
          </Link>
          <Link to="/mon-compte" className="btn btn-outline-primary">
            Mon compte
          </Link>
        </div>
      </div>

      <style>{`
        @media print {
          /* Hide everything except our print content */
          body * {
            visibility: hidden;
          }
          .print-content, .print-content * {
            visibility: visible;
          }
          .print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
          }
          
          /* Hide header and footer */
          header, footer, nav {
            display: none !important;
          }
          
          /* Other print styles */
          .container {
            width: 100%;
            max-width: none;
          }
          
          .card {
            border: none !important;
            break-inside: avoid;
          }
          
          .card-body {
            padding: 0;
          }
          
          .btn, .d-print-none {
            display: none !important;
          }
          
          body {
            print-color-adjust: exact;
            -webkit-print-color-adjust: exact;
          }
        }
      `}</style>
    </Layout>
  );
};

export default SaleConfirmationPage;